import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookingAPI, helperAPI, userAPI } from '../services/api';
import subscriptionAPI from '../api/subscriptionAPI';
import { processRazorpayPayment } from '../services/razorpay';
import PaymentMethodSelector from '../components/PaymentMethodSelector';
import SubscriptionModal from '../components/SubscriptionModal';
import AddressManager from '../components/AddressManager';
import './BookingForm.css';

const BookingForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const helperId = searchParams.get('helperId');

  const [formData, setFormData] = useState({
    helperId: helperId || '',
    service: '',
    date: '',
    startTime: '09:00',
    endTime: '11:00',
    customerAddress: '',
    specialRequests: '',
    paymentMethod: 'online',
    serviceOptions: {}
  });

  const [selectedHelper, setSelectedHelper] = useState(null);
  const [availableHelpers, setAvailableHelpers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionPricing, setSubscriptionPricing] = useState(null);

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const services = [
    { value: 'sweeping-mopping', label: 'Sweeping & Mopping', icon: '🧹' },
    { value: 'cooking', label: 'Cooking', icon: '👨‍🍳' },
    { value: 'laundry', label: 'Laundry', icon: '👕' },
    { value: 'gardening', label: 'Gardening', icon: '🌱' },
    { value: 'pet-care', label: 'Pet Care', icon: '🐕' },
    { value: 'elderly-care', label: 'Elderly Care', icon: '👴' },
    { value: 'babysitting', label: 'Babysitting', icon: '👶' },
    { value: 'hair-dresser', label: 'Hair Dresser', icon: '✂️' },
    { value: 'other', label: 'Other', icon: '🔧' }
  ];

  const apartmentTypes = [
    { value: '1bhk', label: '1 BHK', multiplier: 1.0 },
    { value: '2bhk', label: '2 BHK', multiplier: 1.5 },
    { value: '3bhk', label: '3 BHK', multiplier: 2.0 },
    { value: '4bhk', label: '4 BHK', multiplier: 2.5 },
    { value: 'more-than-4bhk', label: 'More than 4 BHK', multiplier: 3.0 }
  ];

  // Generate time slots in 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ value: timeString, label: displayTime });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Calculate duration when start or end time changes
  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    if (endMinutes <= startMinutes) {
      return 0;
    }
    
    return (endMinutes - startMinutes) / 60;
  };

  // Load available helpers when service changes
  useEffect(() => {
    const loadHelpers = async () => {
      if (formData.service) {
        setLoading(true);
        try {
          const response = await helperAPI.getHelpers({ service: formData.service });
          setAvailableHelpers(response.data.helpers);
          
          // If helperId is pre-selected, find and set the helper
          if (helperId) {
            const helper = response.data.helpers.find(h => h._id === helperId);
            if (helper) {
              setSelectedHelper(helper);
            }
          }
        } catch (err) {
          setError('Failed to load helpers. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    loadHelpers();
  }, [formData.service, helperId]);

  // Load specific helper if helperId is provided
  useEffect(() => {
    const loadSpecificHelper = async () => {
      if (helperId && !selectedHelper) {
        try {
          const response = await helperAPI.getHelper(helperId);
          setSelectedHelper(response.data.helper);
          // Pre-select the service if helper has only one
          if (response.data.helper.services.length === 1) {
            setFormData(prev => ({ ...prev, service: response.data.helper.services[0] }));
          }
        } catch (err) {
          setError('Helper not found. Please select a different helper.');
        }
      }
    };

    loadSpecificHelper();
  }, [helperId, selectedHelper]);

  // Calculate pricing when helper, time, or payment method changes
  useEffect(() => {
    const calculatePricing = async () => {
      if (selectedHelper && formData.startTime && formData.endTime && formData.paymentMethod && formData.service) {
        const duration = calculateDuration(formData.startTime, formData.endTime);
        if (duration <= 0) return;

        setLoadingPricing(true);
        try {
          const response = await bookingAPI.calculatePricing({
            helperId: selectedHelper._id,
            startTime: formData.startTime,
            endTime: formData.endTime,
            paymentMethod: formData.paymentMethod,
            service: formData.service,
            serviceOptions: formData.serviceOptions
          });
          setPricing(response.data.pricing);
        } catch (err) {
          console.error('Pricing calculation failed:', err);
          setPricing(null);
        } finally {
          setLoadingPricing(false);
        }
      }
    };

    calculatePricing();
  }, [selectedHelper, formData.startTime, formData.endTime, formData.paymentMethod, formData.service, formData.serviceOptions]);

  // Load user's saved addresses
  useEffect(() => {
    const loadAddresses = async () => {
      try {
        const response = await userAPI.getAddresses();
        if (response.success) {
          setSavedAddresses(response.data.addresses);
          
          // If there's a default address, pre-populate the address field
          const defaultAddress = response.data.addresses.find(addr => addr.isDefault);
          if (defaultAddress) {
            setFormData(prev => ({
              ...prev,
              customerAddress: defaultAddress.fullAddress + (defaultAddress.landmark ? '\nLandmark: ' + defaultAddress.landmark : '')
            }));
            setSelectedAddressId(defaultAddress._id);
          }
        }
      } catch (err) {
        console.error('Error loading addresses:', err);
      }
    };
    
    loadAddresses();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset helper selection when service changes
    if (name === 'service') {
      setSelectedHelper(null);
      setFormData(prev => ({ 
        ...prev, 
        helperId: '',
        serviceOptions: {} // Reset service options when service changes
      }));
      setPricing(null);
    }

    // Validate end time when start time changes
    if (name === 'startTime') {
      const duration = calculateDuration(value, formData.endTime);
      if (duration <= 0) {
        // Automatically set end time to 2 hours after start time
        const [hour, minute] = value.split(':').map(Number);
        const endHour = hour + 2;
        const endTime = `${endHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        setFormData(prev => ({ ...prev, endTime }));
      }
    }
  };

  const handleHelperSelect = (helper) => {
    setSelectedHelper(helper);
    setFormData(prev => ({ ...prev, helperId: helper._id }));
  };

  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({
      ...prev,
      paymentMethod: method
    }));
  };

  const handleServiceOptionChange = (optionName, value) => {
    setFormData(prev => ({
      ...prev,
      serviceOptions: {
        ...prev.serviceOptions,
        [optionName]: value
      }
    }));
  };

  const handleMakeSubscription = async () => {
    if (!selectedHelper || !formData.service) {
      setError('Please select a helper and service first');
      return;
    }

    try {
      // Calculate duration for the time slot
      const duration = calculateDuration(formData.startTime, formData.endTime);
      
      // Calculate subscription pricing based on current form data
      const subscriptionData = {
        helperId: selectedHelper._id,
        services: [{
          serviceType: formData.service,
          serviceOptions: formData.serviceOptions || {}
        }],
        schedule: {
          daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], // Default to weekdays
          timeSlots: [{
            startTime: formData.startTime,
            endTime: formData.endTime,
            duration: duration,
            applicableServices: [formData.service]
          }]
        },
        subscriptionDurationDays: 30
      };

      console.log('Sending subscription data:', subscriptionData); // Debug log
      
      const response = await subscriptionAPI.calculatePricing(subscriptionData);
      console.log('Subscription pricing response:', response); // Debug log
      
      if (response.success) {
        setSubscriptionPricing({
          ...response.pricing,
          helperInfo: response.helperInfo,
          subscriptionData
        });
        setShowSubscriptionModal(true);
        setError(''); // Clear any previous errors
      } else {
        console.error('Subscription pricing failed:', response.message);
        setError(response.message || 'Failed to calculate subscription pricing');
      }
    } catch (error) {
      console.error('Error calculating subscription pricing:', error);
      setError(`Failed to calculate subscription pricing: ${error.message || 'Please try again'}`);
    }
  };

  const validateForm = () => {
    if (!formData.service) {
      setError('Please select a service');
      return false;
    }
    if (!formData.helperId) {
      setError('Please select a helper');
      return false;
    }
    if (!formData.date) {
      setError('Please select a date');
      return false;
    }
    if (!formData.startTime || !formData.endTime) {
      setError('Please select start and end times');
      return false;
    }
    
    const duration = calculateDuration(formData.startTime, formData.endTime);
    if (duration <= 0) {
      setError('End time must be after start time');
      return false;
    }
    if (duration < 0.5) {
      setError('Minimum booking duration is 30 minutes');
      return false;
    }
    if (duration > 8) {
      setError('Maximum booking duration is 8 hours');
      return false;
    }

    // Validate service-specific requirements
    if (formData.service === 'sweeping-mopping' && !formData.serviceOptions.apartmentType) {
      setError('Please select apartment type for sweeping and mopping service');
      return false;
    }

    if (!formData.customerAddress.trim()) {
      setError('Please enter your address');
      return false;
    }
    if (!formData.paymentMethod) {
      setError('Please select a payment method');
      return false;
    }

    // Check if date is in the future
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setError('Please select a future date');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const duration = calculateDuration(formData.startTime, formData.endTime);
      
      const bookingData = {
        helperId: formData.helperId,
        service: formData.service,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        customerAddress: formData.customerAddress,
        specialRequests: formData.specialRequests,
        paymentMethod: formData.paymentMethod,
        totalAmount: pricing?.totalAmount,
        pricing: pricing,
        serviceOptions: formData.serviceOptions
      };

      // Create the booking first
      const response = await bookingAPI.createBooking(bookingData);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create booking');
      }

      // For all payment methods, just show success and redirect to My Bookings
      let successMessage = '';

      if (formData.paymentMethod === 'cash') {
        successMessage = 'Booking created successfully! You will receive a confirmation once the helper accepts your request. Remember to have the exact amount ready for cash payment.';
      } else if (formData.paymentMethod === 'online') {
        successMessage = 'Booking created successfully! You will receive a confirmation once the helper accepts your request. You can make the payment after the helper accepts.';
      }

      alert(successMessage);
      navigate('/my-bookings');
      
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message || 'Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get tomorrow's date as minimum selectable date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Handle address selection
  const handleAddressSelect = (address) => {
    setFormData(prev => ({
      ...prev,
      customerAddress: address.fullAddress + (address.landmark ? '\nLandmark: ' + address.landmark : '')
    }));
    setSelectedAddressId(address._id);
    setShowAddressSelector(false);
  };

  return (
    <div className="booking-form-container">
      <div className="booking-form-header">
        <h1>📅 Book a Helper</h1>
        <p>Fill out the details below to book your preferred helper</p>
      </div>

      <form onSubmit={handleSubmit} className="booking-form">
        {error && <div className="error-message">{error}</div>}

        {/* Service Selection */}
        <div className="form-group">
          <label htmlFor="service">Service Type *</label>
          <div className="service-grid">
            {services.map(service => (
              <label
                key={service.value}
                className={`service-option ${formData.service === service.value ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="service"
                  value={service.value}
                  checked={formData.service === service.value}
                  onChange={handleInputChange}
                />
                <span className="service-icon">{service.icon}</span>
                <span className="service-label">{service.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Helper Selection */}
        {formData.service && (
          <div className="form-group">
            <label>Select Helper *</label>
            {loading ? (
              <div className="loading">Loading available helpers...</div>
            ) : availableHelpers.length === 0 ? (
              <div className="no-helpers">No helpers available for this service in your area.</div>
            ) : (
              <div className="helpers-grid">
                {availableHelpers.map(helper => (
                  <div
                    key={helper._id}
                    className={`helper-card ${selectedHelper?._id === helper._id ? 'selected' : ''}`}
                    onClick={() => handleHelperSelect(helper)}
                  >
                    <div className="helper-info">
                      <h4>{helper.name}</h4>
                      <p className="helper-location">📍 {helper.location}</p>
                      <p className="helper-rate">💰 ₹{helper.hourlyRate}/hour</p>
                      <div className="helper-rating">
                        ⭐ {helper.rating.toFixed(1)} ({helper.totalReviews} reviews)
                      </div>
                      {helper.bio && (
                        <p className="helper-bio">{helper.bio.substring(0, 100)}...</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Service-Specific Options */}
        {formData.service === 'sweeping-mopping' && (
          <div className="form-group">
            <label>Apartment Type *</label>
            <div className="apartment-grid">
              {apartmentTypes.map(apt => (
                <label
                  key={apt.value}
                  className={`apartment-option ${formData.serviceOptions.apartmentType === apt.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="apartmentType"
                    value={apt.value}
                    checked={formData.serviceOptions.apartmentType === apt.value}
                    onChange={(e) => handleServiceOptionChange('apartmentType', e.target.value)}
                  />
                  <span className="apartment-label">{apt.label}</span>
                  {selectedHelper && (
                    <span className="apartment-rate">
                      ₹{Math.round(selectedHelper.hourlyRate * apt.multiplier)}/hr
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Date and Time */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Service Date *</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              min={getTomorrowDate()}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        {/* Time Selection */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startTime">Start Time *</label>
            <select
              id="startTime"
              name="startTime"
              value={formData.startTime}
              onChange={handleInputChange}
              required
            >
              {timeSlots.map(slot => (
                <option key={slot.value} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="endTime">End Time *</label>
            <select
              id="endTime"
              name="endTime"
              value={formData.endTime}
              onChange={handleInputChange}
              required
            >
              {timeSlots.map(slot => (
                <option key={slot.value} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Duration Display */}
        {formData.startTime && formData.endTime && (
        <div className="form-group">
            <div className="duration-display">
              <span className="duration-label">Duration: </span>
              <span className="duration-value">
                {calculateDuration(formData.startTime, formData.endTime).toFixed(1)} hour{calculateDuration(formData.startTime, formData.endTime) !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Address */}
        <div className="form-group">
          <div className="address-field-header">
            <label htmlFor="customerAddress">Your Address *</label>
            <button 
              type="button" 
              className="address-selector-btn"
              onClick={() => setShowAddressSelector(!showAddressSelector)}
            >
              {showAddressSelector ? 'Hide Saved Addresses' : 'Use Saved Address'}
            </button>
          </div>
          
          {showAddressSelector && savedAddresses.length > 0 && (
            <div className="saved-addresses-selector">
              <AddressManager 
                onSelect={handleAddressSelect} 
                selectedAddressId={selectedAddressId}
                mode="select"
              />
            </div>
          )}
          
          <textarea
            id="customerAddress"
            name="customerAddress"
            value={formData.customerAddress}
            onChange={handleInputChange}
            placeholder="Please provide your complete address where the service is needed"
            rows="3"
            required
          />
        </div>

        {/* Special Requests */}
        <div className="form-group">
          <label htmlFor="specialRequests">Special Requests (Optional)</label>
          <textarea
            id="specialRequests"
            name="specialRequests"
            value={formData.specialRequests}
            onChange={handleInputChange}
            placeholder="Any specific requirements or instructions for the helper..."
            rows="3"
          />
        </div>

        {/* Monthly Subscription Option */}
        {selectedHelper && pricing && (
          <div className="form-group">
            <button
              type="button"
              className="subscription-btn-secondary"
              onClick={handleMakeSubscription}
              disabled={isSubmitting}
            >
              📅 Make it Monthly Subscription (Optional)
            </button>
            <p className="subscription-note">
              <em>Note: Only online payment is accepted for monthly subscriptions</em>
            </p>
          </div>
        )}

        {/* Payment Method Selection */}
        {selectedHelper && (
          <PaymentMethodSelector
            selectedMethod={formData.paymentMethod}
            onMethodChange={handlePaymentMethodChange}
            pricing={pricing}
            showPricing={true}
          />
        )}

        {/* Live Pricing Summary */}
        {selectedHelper && pricing && (
          <div className="booking-summary">
            <h3>📊 Booking Summary</h3>
            <div className="summary-row">
              <span>Helper:</span>
              <span>{selectedHelper.name}</span>
            </div>
            <div className="summary-row">
              <span>Service:</span>
              <span>{services.find(s => s.value === formData.service)?.label}</span>
            </div>
            <div className="summary-row">
              <span>Duration:</span>
              <span>{calculateDuration(formData.startTime, formData.endTime).toFixed(1)} hour{calculateDuration(formData.startTime, formData.endTime) !== 1 ? 's' : ''}</span>
            </div>
            <div className="summary-row">
              <span>Payment Method:</span>
              <span>{formData.paymentMethod === 'cash' ? '💵 Cash Payment' : '💳 Online Payment'}</span>
            </div>
            <div className="summary-row total">
              <span>Total Amount:</span>
              <span>₹{pricing.totalAmount}</span>
            </div>
            
            {loadingPricing && (
              <div className="pricing-loading">Calculating pricing...</div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
        <button
          type="submit"
          className="submit-btn"
            disabled={isSubmitting || !selectedHelper || !pricing}
        >
            {isSubmitting ? 'Creating Booking...' : `🎯 Book Now - ₹${pricing?.totalAmount || 0}`}
        </button>
        </div>
      </form>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          pricing={subscriptionPricing}
          helper={selectedHelper}
          initialFormData={formData}
        />
      )}
    </div>
  );
};

export default BookingForm; 